import { ButtonType } from '@/components/CmgButton/cmg-button';
import { formatOfferEndsDate } from '@/helpers';
import { CustomerProfile } from '@/models';
import { Campaign, GroupCampaign, CampaignTypes, isGroupCampaign } from '@/models/reward';
import { Routes } from '@/router';
import { ExtraOptinEvent } from '@/services/analytics/events/extra-optin-event';
import { AnalyticsEventType } from '@/services/analytics/platforms/adobe-analytics/models';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
@Component({

  name: 'CmgRewardExtrasCampaignCard',
})
export default class CmgRewardExtrasCampaignCard extends Vue {
  isSubmitted: boolean = false;

  @Prop({ required: true, type: Object })
  campaign!: Campaign;

  get progressPercentage(): number {
    return this.campaign.isMultiStepCampaign
      ? (this.campaign.currentStep / this.campaign.totalNumberOfSteps) * 100
      : 0;
  }   

  get activatedGroupCampaign(): Campaign | null{
    if (this.isGroupExtra && this.isOptedIn) {
      const groupCampaign = <GroupCampaign> this.campaign;
      return groupCampaign.campaigns.filter(x => x.isOptedIn)[0];
    }
    return null;
  }

  get titleText(): string {
    if (!this.isGroupExtra) { return this.campaign.title.toUpperCase(); }
    if (this.isGroupExtra && !this.activatedGroupCampaign) {
      const groupCampaign = <GroupCampaign> this.campaign;
      return groupCampaign.groupName;
    }
    if (this.isOptedIn && this.activatedGroupCampaign) { return this.activatedGroupCampaign.title.toUpperCase(); }
    return '';
  }

  get expirationDate(): string {
    if (this.campaign.endDate) {
      return formatOfferEndsDate(new Date(this.campaign.endDate));
    }
  
    return '';
  }

  get descriptionText(): string {
    if (!this.isGroupExtra) { return this.campaign.description; }
    if (this.isOptedIn && this.activatedGroupCampaign) { return this.activatedGroupCampaign.description; }
    if (this.isGroupExtra && !this.activatedGroupCampaign) {
      const groupCampaign = <GroupCampaign> this.campaign;
      return groupCampaign.description;
    }
    return '';
  }

  get extraPoints(): string {
      return this.campaign.extraPoints > 0 
        ? `+${this.campaign.extraPoints}`
        : '';
  } 

  get isExtra(): boolean {
    return !this.campaign.isMultiStepCampaign;
  }

  get isOptinExtra(): boolean {
    return this.campaign.campaignType?.toLowerCase() === CampaignTypes.OptIn;
  }

  get isGroupExtra(): boolean {
    return isGroupCampaign(this.campaign);
  }

  get isSummerExtra(): boolean {
    return this.campaign.campaignType?.toLowerCase() === CampaignTypes.SummerExtra;
  }

  get expirationText(): string {
    return `Offer ends ${this.expirationDate}`;
  }

  get isOptedIn(): boolean {
    return this.campaign.isOptedIn;
  }

  get showSecondaryButton(): boolean {
    return (this.isOptedIn || (!this.isOptinExtra && !this.isGroupExtra && !this.isSummerExtra));
  }

  get ctaText(): string {
    if (this.isGroupExtra && !this.isOptedIn) { return 'SEE YOUR OPTIONS'; }
    if ((this.isOptinExtra || this.isSummerExtra) && !this.isOptedIn) { return 'ACTIVATE'; }
    if (this.campaign.ctaText) { return this.campaign.ctaText.toUpperCase(); }
    if (this.campaign.ctaType?.toLowerCase() === 'grouporder') { return 'START YOUR GROUP ORDER'; }
    if (this.isSummerExtra && this.isOptedIn) { return 'GO TO DASHBOARD'}
    return 'START YOUR ORDER'; // individual order extra for non-optin extra or an activated opt-in extra
  }

  get checkMarkUrl(): string {
    return this.$appStore.state.cms.images.icons.activatedCheckmark.default;
  }

  get medallionBackgroundUrl(): string {
    if (this.campaign.medallionText) {
      return this.$appStore.state.cms.images.backgrounds.pointsMultiplier.default;
    } else {
      return this.$appStore.state.cms.images.backgrounds.pointsEarnedRed.default;
    }
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  onClickTermsAndConditions(event: Event) {
    if (this.isSummerExtra) {
      event.stopPropagation();
      window.location.href = `${window.location.origin}/summer-of-extras-terms`;
    }
  }

  onClickCampaignCard() {
    if (this.isGroupExtra) {
      const groupCampaign = <GroupCampaign> this.campaign;
      if (this.isOptedIn) {        
        const campaign = groupCampaign.campaigns.filter(x => x.isOptedIn)[0];      
        this.$services.ui.setModalVisibility('rewardExtrasCampaignDetail', true, { campaign }); 
      } else {
        this.$services.ui.setModalVisibility('rewardExtrasGroupCampaignDetail', true, { groupCampaign });
      }      
    } else if (this.isSummerExtra) {
      this.showEducator();
    } else {
      const campaign = this.campaign;
      this.$services.ui.setModalVisibility('rewardExtrasCampaignDetail', true, { campaign });
    }
  }

  onClickCta(event: Event) {
    event.stopPropagation();
    if (this.isSummerExtra) {
      if (!this.isOptedIn) {
        this.showEducator();
      } else {
        this.$router.push({ name: Routes.Leaderboard });
      }
    } else if (this.isOptinExtra && !this.isOptedIn) {
      this.isSubmitted = true;
      let myCampaign = this.campaign;
      
      this.$services.reward.optIntoCampaign(this.campaign.id).then(async data => {
        if(data?.awarded) {
          myCampaign.isOptedIn = true;

          if (!this.customer?.optedIntoExtras) {
            await this.$services.customer.updateCustomer({
              optedIntoExtras: true
            });
            this.customer!.optedIntoExtras = true;
          }

          this.$services.analytics.send(new ExtraOptinEvent(AnalyticsEventType.ActivateOffer, this.campaign, false)); 
        }
      })
      .catch(error => {
        const onClose = () => {
          this.$services.ui.setModalVisibility('basicError', false);
        };

        this.$services.ui.setModalVisibility('basicError', true, {
          header: `OUR BAD`,
          content: 'There was a problem opting into your extra. Give it a sec and try again.',
          background: 'white',
          options: [
            {
              text: 'Okay',
              type: ButtonType.Primary,
              onClick: onClose,
            },
          ],
          onClose,
        });
      })
      .finally(() => {
        this.isSubmitted = false;
        return false;
      })
    } else if (this.isGroupExtra) {
      const groupCampaign = <GroupCampaign> this.campaign;
      if (!this.isOptedIn) {        
        this.$services.ui.setModalVisibility('rewardExtrasGroupCampaignDetail', true, { groupCampaign });
      } else {
        this.addLinkClickedEvent();

        switch (groupCampaign.campaigns[0]?.ctaType) {
          case 'GroupOrder':
            this.$router.push({ name: Routes.GroupOrderCreate });
            break;
          case 'Order':
            this.$router.push({ name: Routes.Order, hash: '#menu' });
            break;
          default:
            this.$router.push({ name: Routes.Order, hash: '#menu' });
            break;
        }
      }
    } else {
      this.addLinkClickedEvent();

      if (this.campaign.ctaUrl) {
        window.location.href = this.campaign.ctaUrl;
        return;
      }

      switch (this.campaign.ctaType) {
        case 'GroupOrder':
          this.$router.push({ name: Routes.GroupOrderCreate });
          break;
        case 'Order':
          this.$router.push({ name: Routes.Order, hash: '#menu' });
          break;
        default:
          this.$router.push({ name: Routes.Order, hash: '#menu' });
          break;
      }
    }
    return false;
  } 
  
  addLinkClickedEvent() {
    this.$services.analytics.addLinkClickedEventToStore({
      linkClicks: { value: 1 },
      linkSection: 'rewards-extra',
      linkTitle: `rewardsExtra - ${this.campaign.title.toUpperCase()}`,
      name:  this.ctaText,            
      type: 'other'
    }, '');
  }

  private showEducator(): void {
    const campaign = this.campaign;
    const isActivated = this.campaign.isOptedIn;
    if (campaign) {
      this.$services.ui.setModalVisibility('summerLeaderboardEducator', true, { campaign, isActivated });
    }
  }
}